import { useState, useEffect, useRef, Fragment } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Search,
  Download,
  SlidersHorizontal,
  X,
  Phone,
  MoreVertical,
  ChevronRight,
  RefreshCw,
  UserPlus,
  Check,
  Star,
  Calendar,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { formatPhone, phoneToDigits } from '../../../lib/format.js';
import { Button } from '../../../components/ui/Button.js';
import { LandlordCard } from '../../../components/landlords/LandlordCard.js';
import { LandlordFilters, type FilterState, EMPTY_FILTERS, countActiveFilters } from '../../../components/landlords/LandlordFilters.js';
import { LandlordDrawer } from '../../../components/landlords/LandlordDrawer.js';
import { SkeletonCard } from '../../../components/ui/Skeleton.js';
import { StageBadge } from '../../../components/ui/Badge.js';
import { DataTable } from '../../../components/data-table/DataTable.js';
import { Pagination } from '../../../components/data-table/Pagination.js';
import { Popover } from '../../../components/ui/Popover.js';
import {
  ColumnManager,
  loadHiddenColumns,
  saveHiddenColumns,
  migrateLegacyHiddenColumns,
  type ColumnDef,
} from '../../../components/data-table/ColumnManager.js';
import { useDebounce } from '../../../hooks/useDebounce.js';
import { useActivityLog } from '../../../hooks/useActivityLog.js';
import { useAuth } from '../../../lib/auth.js';
import { landlordsApi, exportCsvUrl, buildingsApi, tagsApi, areasApi, agentsApi, assignmentsApi, unitsApi } from '../../../lib/api.js';
import { useToast } from '../../../components/ui/Toast.js';
import { cn } from '../../../lib/cn.js';
import type { LandlordListItem, UnitListItem } from '@imza/shared';

export const Route = createFileRoute('/_auth/landlords/')({
  component: LandlordsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    q: (search.q as string) || '',
    mode: (search.mode as string) || 'smart',
    area_id: (search.area_id as string) || '',
    building: (search.building as string) || '',
    stage: (search.stage as string) || '',
    tag_id: (search.tag_id as string) || '',
    has_contact: (search.has_contact as string) || '',
    is_starred: (search.is_starred as string) || '',
    // View tab is read separately from window.location (see getViewFromUrl
    // below) to avoid forcing every existing <Link to="/landlords">
    // call-site to declare a `view` prop in their search object.
  }),
});

// Read the view tab directly from the URL ?view= param. Default depends
// on whether an area is filtered: with no area filter, the building-card
// grid (grouped by area) is the landing view; with an area selected, the
// list is the default unless ?view= overrides. Sherzod 2026-05-03.
function getViewFromUrl(): 'list' | 'stats' | 'buildings' {
  if (typeof window === 'undefined') return 'buildings';
  const params = new URLSearchParams(window.location.search);
  const v = params.get('view');
  if (v === 'list' || v === 'stats' || v === 'buildings') return v;
  // No explicit ?view= — pick by whether an area is filtered.
  return params.get('area_id') ? 'list' : 'buildings';
}

// :v5 per-contact (Phase 1 BL07) → :v6 reverted to per-unit per Sherzod
// 2026-05-03 ("I need unit number / bedroom / view to be shown").
const TABLE_ID = 'landlords:v8';
// Legacy keys to migrate forward (one-time port; see migrateLegacyHiddenColumns).
const LEGACY_TABLE_IDS = ['landlords:v7', 'landlords:v6'] as const;

// Sherzod 2026-05-03 (revert from Phase 1 of Backlog 07): per-UNIT rows back.
// Each row = one unit (so Alokozay shows 202 rows). Unit number, bedroom,
// view are visible columns again. Bedroom + View editable on admin double-
// click. Per-contact dedup at the LIST level deferred to Backlog 07 Phase 4
// (drawer rewrite shows per-unit cards inside a per-contact drawer).
// Single source of truth for both the ColumnManager dropdown AND the
// DataTable header rendering. Sherzod 2026-05-05: previously the inline
// columns prop on DataTable diverged from this list, causing toggles
// for Total Units + Comment to render but the headers never to appear.
const ALL_COLUMNS: ColumnDef[] = [
  { key: 'star',          label: '',              required: true,                  width: '44px' },
  { key: 'name',          label: 'Owner',         required: true, sortable: true,  width: '260px' },
  { key: 'total_units',   label: 'Total Units',                   sortable: true,  width: '100px' },
  { key: 'building',      label: 'Building',                      sortable: true,  width: '220px' },
  { key: 'unit_no',       label: 'Unit',                          sortable: true,  width: '130px' },
  { key: 'beds',          label: 'Bedroom',                       sortable: true,  width: '110px' },
  { key: 'view',          label: 'View',                          sortable: true,  width: '120px' },
  { key: 'phone',         label: 'Phone',                                          width: '170px' },
  { key: 'actions',       label: 'Actions',                                        width: '110px' },
  { key: 'stage',         label: 'Status',                        sortable: true,  width: '130px' },
  { key: 'imported',      label: 'Date',                          sortable: true,  width: '120px' },
  { key: 'comment',       label: 'Comment',                                        width: '240px' },
  { key: 'email',         label: 'Email',                                          width: '200px' },
  { key: 'last_activity', label: 'Last Activity',                 sortable: true,  width: '140px' },
];

// Hide email + last_activity by default (mostly empty for Bayut imports).
// Toggle on via the gear menu when needed.
const DEFAULT_HIDDEN_COLUMNS = new Set<string>(['email', 'last_activity']);

// Stage dropdown — order, label, and dot-color (Sherzod-specified palette
// 2026-05-03; matches Badge.tsx STAGE_STYLES + LandlordDrawer.tsx STAGE_DOT).
const STAGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'new',          label: 'New' },
  { value: 'sale',         label: 'Sale' },
  { value: 'sold',         label: 'Sold' },
  { value: 'rent',         label: 'Rent' },
  { value: 'rented',       label: 'Rented' },
  { value: 'potential',    label: 'Potential' },
  { value: 'unavailable',  label: 'Unavailable' },
  { value: 'no_sale',      label: 'No Sale' },
  { value: 'no_answer',    label: 'No Answer' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'agent',        label: 'Agent' },
];

const STAGE_DOT_BG: Record<string, string> = {
  new:          'bg-blue-500',
  sale:         'bg-green-500',
  sold:         'bg-red-500',
  rent:         'bg-orange-500',
  rented:       'bg-rose-700',
  potential:    'bg-purple-500',
  unavailable:  'bg-gray-400',
  no_sale:      'bg-red-500',
  no_answer:    'bg-yellow-500',
  wrong_number: 'bg-red-700',
  agent:        'bg-sky-500',
};

// Widths now live on ALL_COLUMNS (single source of truth). Removed the
// separate COLUMN_WIDTHS map 2026-05-05 — see ColumnDef.width.

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const MODE_CHIPS = [
  { value: 'smart', label: 'Smart', icon: '🔍' },
  { value: 'building_unit', label: 'Building + Unit', icon: '🏢' },
  { value: 'name', label: 'Owner', icon: '👤' },
  { value: 'phone', label: 'Mobile', icon: '📱' },
] as const;

function LandlordsPage() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const urlSearch = Route.useSearch();

  const [filters, setFilters] = useState<FilterState>(() => ({
    ...EMPTY_FILTERS,
    q: urlSearch.q || '',
    mode: (urlSearch.mode as FilterState['mode']) || 'smart',
    area_id: urlSearch.area_id || '',
    building: urlSearch.building || '',
    stage: urlSearch.stage || '',
    tag_id: urlSearch.tag_id || '',
    has_contact: urlSearch.has_contact || '',
    is_starred: urlSearch.is_starred || '',
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  // activeView is locally controlled by the in-page tab buttons (Stats /
  // Buildings / Top Investors), but ALSO synced FROM the URL ?view= param
  // (effect below). When the user clicks the Landlords main-nav button —
  // which navigates to `/landlords` with no params — view becomes '' and
  // the effect resets us back to the default list. This is the explicit
  // fix Sherzod asked for: clean list always opens first.
  const [activeView, setActiveView] = useState<'list' | 'stats' | 'buildings'>(() => getViewFromUrl());
  // Buildings view (All Areas grouped) — area sort: alpha or by record count.
  const [buildingsAreaSort, setBuildingsAreaSort] = useState<'alpha' | 'count'>('alpha');

  // URL → activeView sync. Runs whenever urlSearch changes (including
  // when the Landlords main-nav button drops us back at /landlords with
  // no params). Resets the Top-Investors sort if it was sticky.
  useEffect(() => {
    const v = getViewFromUrl();
    setActiveView(v);
    if (v === 'list') {
      setFilters((f) => f.sort === 'units_desc' ? { ...f, sort: 'name' } : f);
    }
  }, [urlSearch]);

  // Ref for mobile filter popover trigger only (desktop uses inline filters)
  const filterTriggerMobileRef = useRef<HTMLButtonElement>(null);

  // Column visibility — load from localStorage; on a fresh device (no saved
  // prefs), apply DEFAULT_HIDDEN_COLUMNS. v8 migration ports forward any
  // legacy v6/v7 hidden-set so the user doesn't lose their toggles.
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    for (const legacy of LEGACY_TABLE_IDS) {
      if (migrateLegacyHiddenColumns(legacy, TABLE_ID, ALL_COLUMNS)) break;
    }
    const loaded = loadHiddenColumns(TABLE_ID, ALL_COLUMNS);
    if (loaded.size === 0 && !localStorage.getItem(`imza:col:${TABLE_ID}`)) {
      // First visit on this browser — apply defaults
      return new Set(DEFAULT_HIDDEN_COLUMNS);
    }
    return loaded;
  });

  function handleColumnToggle(key: string, hidden: boolean) {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (hidden) next.add(key);
      else next.delete(key);
      saveHiddenColumns(TABLE_ID, next);
      return next;
    });
  }

  // Sort state — back to per-unit. Default 'name' ASC keeps the page
  // consistent across endpoint flips (both /api/landlords and /api/units
  // accept 'name' as a sort).
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function handleSort(key: string) {
    if (sortBy === key) {
      // Sherzod 2026-05-06: total_units cycles desc → asc → cleared
      // (matches Top Investors toggle-off semantics). Other columns
      // stay 2-state asc↔desc as before.
      if (key === 'total_units' && sortDir === 'asc') {
        setSortBy('name');
        setSortDir('asc');
      } else {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      }
    } else {
      setSortBy(key);
      // Sensible default direction per column: text columns asc (A→Z),
      // numeric/date columns desc (newest/biggest first).
      const descByDefault = new Set(['imported', 'last_activity', 'beds', 'unit_no', 'total_units']);
      setSortDir(descByDefault.has(key) ? 'desc' : 'asc');
    }
  }

  const debouncedQ = useDebounce(filters.q, 250);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setSelectedRows(new Set());
  }, [debouncedQ, filters.mode, filters.area_id, filters.building, filters.tag_id, filters.stage, filters.has_contact, filters.needs_review, filters.updated_since, sortBy, sortDir]);

  // Global search keyboard shortcut
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
        setShowFilters(false); // closes mobile filter popover
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Map column key → /api/units sort vocabulary.
  // Backend accepts: name|building|unit|view|beds|stage|imported|recent|total_units.
  const apiSortMap: Record<string, string> = {
    name:          'name',
    building:      'building',
    unit_no:       'unit',
    view:          'view',
    beds:          'beds',
    stage:         'stage',
    imported:      'imported',
    last_activity: 'recent',
    total_units:   'total_units',
  };
  const apiSort = apiSortMap[sortBy] ?? 'name';

  const searchParams: Record<string, string | number | boolean | undefined> = {
    q: debouncedQ || undefined,
    mode: filters.mode,
    area_id: filters.area_id ? Number(filters.area_id) : undefined,
    building: filters.building || undefined,
    tag_id: filters.tag_id ? Number(filters.tag_id) : undefined,
    stage: filters.stage || undefined,
    has_contact: filters.has_contact !== '' ? filters.has_contact === 'true' : undefined,
    needs_review: filters.needs_review !== '' ? filters.needs_review === 'true' : undefined,
    is_starred: filters.is_starred !== '' ? filters.is_starred === 'true' : undefined,
    sort: apiSort,
    dir: sortDir,
    page,
    pageSize,
  };

  // Per-unit endpoint — one row per unit. Each row carries its own unit_no,
  // beds, view, and parent landlord rolled up for display.
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['units', searchParams],
    queryFn: () => landlordsApi.listUnits(searchParams),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Dropdown data for inline desktop filters
  const { data: areasData } = useQuery({
    queryKey: ['areas'],
    queryFn: () => areasApi.list(),
    staleTime: 5 * 60_000,
  });
  const areas = areasData?.items ?? [];

  const { data: buildingsData } = useQuery({
    queryKey: ['buildings-simple', filters.area_id],
    queryFn: () => buildingsApi.list(filters.area_id ? Number(filters.area_id) : undefined),
    staleTime: 5 * 60_000,
  });
  const buildings = buildingsData?.items ?? [];
  const buildingsTotal = buildingsData?.total ?? 0;

  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
    staleTime: 5 * 60_000,
  });
  const tags = tagsData?.items ?? [];

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['stats-overview'],
    queryFn: async () => {
      const res = await fetch('/api/stats/overview', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: activeView === 'stats',
    staleTime: 5 * 60 * 1000,
  });

  function handleExport() {
    const url = exportCsvUrl({
      q: debouncedQ || undefined,
      mode: filters.mode,
      area_id: filters.area_id || undefined,
      stage: filters.stage || undefined,
    });
    window.open(url, '_blank');
    toast('Export started', 'info');
  }

  function handleBulkAssign() {
    if (selectedRows.size === 0) {
      toast('Select rows first using the checkboxes', 'info');
      return;
    }
    toast(`Bulk assign for ${selectedRows.size} landlords — coming soon`, 'info');
  }

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: ['landlords'] });
  }

  // Row selection helpers
  function toggleRow(id: number) {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Per-unit: rows keyed by unitId.
  const allSelected = items.length > 0 && items.every((item) => selectedRows.has(item.unitId));

  function handleSelectAll(checked: boolean) {
    if (checked) {
      setSelectedRows(new Set(items.map((i) => i.unitId)));
    } else {
      setSelectedRows(new Set());
    }
  }

  // Active filter count (excluding q and mode)
  const activeFilterCount = countActiveFilters(filters);

  // Clear filters and close popover
  function handleResetFilters() {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setShowFilters(false);
  }

  return (
    <div className="flex flex-col h-full">

      {/* ── MOBILE LAYOUT ─────────────────────────────────────────────── */}

      {/* Mobile: big search bar + filter button */}
      <div className="sm:hidden bg-white border-b border-border px-4 pt-4 pb-3 space-y-2">
        {/* Search bar — full width, h-12 */}
        <div className="relative flex items-center">
          <Search className="absolute left-3.5 w-5 h-5 text-secondary pointer-events-none" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search landlords…"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className={cn(
              'w-full h-12 pl-10 pr-10 rounded-xl border text-sm',
              'border-border bg-white text-primary-text placeholder:text-secondary',
              'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
              'transition-shadow'
            )}
            aria-label="Search landlords"
          />
          {filters.q && (
            <button
              onClick={() => setFilters((f) => ({ ...f, q: '' }))}
              className="absolute right-3 p-1 rounded-full text-secondary hover:text-primary-text transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Mode chips + Filter trigger row */}
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5 no-scrollbar">
          {/* Mode chips */}
          {MODE_CHIPS.map((m) => (
            <button
              key={m.value}
              onClick={() => setFilters((f) => ({ ...f, mode: m.value as FilterState['mode'] }))}
              className={cn(
                'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border min-h-[36px]',
                filters.mode === m.value
                  ? 'bg-primary text-white border-primary'
                  : 'border-border text-secondary hover:border-primary hover:text-primary-text'
              )}
            >
              {m.icon} {m.label}
            </button>
          ))}

          {/* Divider */}
          <div className="w-px h-5 bg-border flex-shrink-0 mx-0.5" aria-hidden="true" />

          {/* Filter trigger */}
          <button
            ref={filterTriggerMobileRef as React.RefObject<HTMLButtonElement>}
            onClick={() => setShowFilters((v) => !v)}
            className={cn(
              'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border min-h-[36px]',
              (showFilters || activeFilterCount > 0)
                ? 'border-primary text-primary bg-primary/5'
                : 'border-border text-secondary hover:border-primary hover:text-primary-text'
            )}
            aria-label={`Filters${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
            aria-expanded={showFilters}
            aria-haspopup="true"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary text-white text-[10px] rounded-full px-1.5 py-0 leading-tight">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile filter popover */}
      <Popover
        open={showFilters}
        onClose={() => setShowFilters(false)}
        triggerRef={filterTriggerMobileRef as React.RefObject<HTMLElement>}
        minWidth={260}
        maxWidth={320}
      >
        <LandlordFilters
          filters={filters}
          onChange={setFilters}
          onReset={handleResetFilters}
          resultCount={total}
        />
      </Popover>

      {/* Mobile: result count */}
      <div className="sm:hidden bg-white border-b border-border px-4 py-1.5 flex items-center justify-between">
        <p className="text-xs text-secondary">
          {isFetching && !isLoading ? 'Updating…' : (
            <><span className="font-semibold text-primary-text">{total.toLocaleString()}</span> landlords</>
          )}
        </p>
        {hasRole('manager') && (
          <Button variant="ghost" size="sm" onClick={handleExport} className="text-secondary">
            <Download className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Mobile: card list */}
      <div className="sm:hidden flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <Search className="w-10 h-10 text-secondary mb-3" />
            <p className="font-semibold text-primary-text">No landlords found</p>
            <p className="text-sm text-secondary mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {items.map((unit) => (
              <UnitMobileCard
                key={unit.unitId}
                unit={unit}
                onClick={() => setSelectedId(unit.landlordId)}
              />
            ))}
          </div>
        )}
        {/* Mobile pagination */}
        {totalPages > 1 && (
          <div className="bg-white border-t border-border px-4 py-3 flex items-center justify-between gap-3">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <p className="text-xs text-secondary">Page {page} of {totalPages}</p>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>

      {/* ── DESKTOP LAYOUT ──────────────────────────────────────────────── */}
      <div className="hidden sm:flex flex-col flex-1 overflow-hidden">

        {/* Desktop inline filter bar */}
        <div className="bg-white border-b border-border relative z-20">

          {/* Row 1: Filter dropdowns */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-wrap">
            {/* Area dropdown */}
            <select
              value={filters.area_id}
              onChange={(e) => setFilters((f) => ({ ...f, area_id: e.target.value, building: '' }))}
              className="h-11 pl-4 pr-10 py-2.5 rounded-xl border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_12px_center] bg-no-repeat min-w-[140px]"
              aria-label="Filter by area"
            >
              <option value="">All Areas ({total.toLocaleString()})</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>

            {/* Building dropdown */}
            <select
              value={filters.building}
              onChange={(e) => setFilters((f) => ({ ...f, building: e.target.value }))}
              className="h-11 pl-4 pr-10 py-2.5 rounded-xl border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_12px_center] bg-no-repeat min-w-[150px]"
              aria-label="Filter by building"
            >
              <option value="">All Buildings ({buildingsTotal.toLocaleString()})</option>
              {buildings.map((b) => (
                <option key={b.name} value={b.name}>{b.name}</option>
              ))}
            </select>

            {/* Status dropdown */}
            <select
              value={filters.stage}
              onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))}
              className="h-11 pl-4 pr-10 py-2.5 rounded-xl border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_12px_center] bg-no-repeat min-w-[110px]"
              aria-label="Filter by status"
            >
              <option value="">Status</option>
              <option value="available">Available</option>
              <option value="not_available">Not Available</option>
              <option value="sold">Sold</option>
            </select>

            {/* Tags dropdown */}
            <select
              value={filters.tag_id}
              onChange={(e) => setFilters((f) => ({ ...f, tag_id: e.target.value }))}
              className="h-11 pl-4 pr-10 py-2.5 rounded-xl border border-border text-sm text-primary-text bg-white focus:outline-none focus:border-primary appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_12px_center] bg-no-repeat min-w-[110px]"
              aria-label="Filter by tag"
            >
              <option value="">All Tags</option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {/* Hide unreachable checkbox */}
            <label className="flex items-center gap-1.5 text-xs text-secondary cursor-pointer whitespace-nowrap select-none">
              <input
                type="checkbox"
                checked={filters.has_contact === 'true'}
                onChange={(e) => setFilters((f) => ({ ...f, has_contact: e.target.checked ? 'true' : '' }))}
                className="w-3.5 h-3.5 rounded border-border accent-primary"
                aria-label="Hide unreachable landlords"
              />
              Hide unreachable
            </label>

            {/* Right: page size + column manager + actions */}
            <div className="ml-auto flex items-center gap-1.5">
              {/* Calendar date filter */}
              <div className="relative flex items-center">
                <button
                  onClick={() => {
                    const inp = document.getElementById('landlord-date-filter') as HTMLInputElement | null;
                    if (inp) { inp.showPicker ? inp.showPicker() : inp.click(); }
                  }}
                  className={cn(
                    'p-2 rounded-lg border transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center',
                    filters.updated_since
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-secondary hover:text-primary hover:border-primary'
                  )}
                  aria-label="Filter by date"
                >
                  <Calendar className="w-4 h-4" />
                </button>
                <input
                  id="landlord-date-filter"
                  type="date"
                  className="absolute top-full left-0 mt-1 opacity-0 pointer-events-none w-0 h-0"
                  value={filters.updated_since || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, updated_since: e.target.value }))}
                  tabIndex={-1}
                />
                {filters.updated_since && (
                  <button
                    onClick={() => setFilters((f) => ({ ...f, updated_since: '' }))}
                    className="ml-1 text-[11px] text-primary bg-primary/10 px-2 py-1 rounded-full font-medium flex items-center gap-1 hover:bg-primary/20 transition-colors"
                  >
                    {filters.updated_since}
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              {hasRole('manager') && (
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="w-3.5 h-3.5" />
                  Export
                </Button>
              )}
              {hasRole('manager') && selectedRows.size > 0 && (
                <Button size="sm" onClick={handleBulkAssign}>
                  <UserPlus className="w-3.5 h-3.5" />
                  Assign ({selectedRows.size})
                </Button>
              )}
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="h-11 pl-4 pr-10 py-2.5 rounded-xl border border-border text-sm text-secondary bg-white focus:outline-none focus:border-primary min-w-[90px] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_12px_center] bg-no-repeat"
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>Show {n}</option>
                ))}
              </select>
              <ColumnManager
                tableId={TABLE_ID}
                columns={ALL_COLUMNS}
                hiddenColumns={hiddenColumns}
                onToggle={handleColumnToggle}
              />
              <KebabMenu onRefresh={handleRefresh} />
            </div>
          </div>

          {/* Row 2: Mode tabs — full-width equal tabs */}
          <div className="flex items-stretch border-b border-border">
            {MODE_CHIPS.map((m) => (
              <button
                key={m.value}
                onClick={() => setFilters((f) => ({ ...f, mode: m.value as FilterState['mode'] }))}
                className={cn(
                  'flex-1 py-2.5 text-sm font-medium text-center transition-colors border-b-2',
                  filters.mode === m.value
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-secondary hover:text-primary-text hover:bg-hover-bg'
                )}
              >
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {/* Row 3: Big search + quick chips */}
          <div className="px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary mb-1.5">
              Search anything — owner, unit, building, nationality
            </p>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                placeholder='e.g. "Ahmed" · "Skyz 304" · "Indian owners" · "unit 101"...'
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                className="w-full h-12 pl-12 pr-10 rounded-xl border border-border text-sm bg-white text-primary-text placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-shadow"
                aria-label="Search landlords"
              />
              {filters.q && (
                <button
                  onClick={() => setFilters((f) => ({ ...f, q: '' }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full text-secondary hover:text-primary-text transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Quick action chips */}
            <div className="flex items-center gap-2.5 mt-3 flex-wrap">
              {/* Top Investors — sorts by name-group portfolio size desc.
                  Preserves all active filters (area, building, status,
                  tag, search, hide-unreachable). Clicking again toggles
                  back to default sort. Sherzod 2026-05-06: removed all
                  is_starred wiring (separate concern). */}
              <button
                onClick={() => {
                  setActiveView('list');
                  if (sortBy === 'total_units') {
                    // Toggle off — back to default sort.
                    setSortBy('name');
                    setSortDir('asc');
                  } else {
                    setSortBy('total_units');
                    setSortDir('desc');
                  }
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors border',
                  activeView === 'list' && sortBy === 'total_units'
                    ? 'bg-primary text-white border-primary'
                    : 'border-border text-secondary hover:border-primary hover:text-primary'
                )}
              >
                🏆 Top Investors
              </button>
              <button
                onClick={() => setActiveView(v => v === 'stats' ? 'list' : 'stats')}
                className={cn(
                  'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors border',
                  activeView === 'stats'
                    ? 'bg-primary text-white border-primary'
                    : 'border-border text-secondary hover:border-primary hover:text-primary'
                )}
              >
                📊 Stats
              </button>
              <button
                onClick={() => setActiveView(v => v === 'buildings' ? 'list' : 'buildings')}
                className={cn(
                  'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors border',
                  activeView === 'buildings'
                    ? 'bg-primary text-white border-primary'
                    : 'border-border text-secondary hover:border-primary hover:text-primary'
                )}
              >
                🏢 Buildings
              </button>
              {activeFilterCount > 0 && (
                <button
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-danger/30 text-sm font-medium text-danger hover:bg-danger/5 transition-colors"
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>

          {/* Building cards grid — shown when area is selected and no building is chosen yet */}
          {filters.area_id && !filters.building && buildings.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-white">
              {/* Summary line */}
              <p className="text-sm font-semibold text-primary-text mb-3">
                {areas.find((a) => String(a.id) === filters.area_id)?.name ?? 'Area'}{' '}
                <span className="text-secondary font-normal">—</span>{' '}
                <span className="text-secondary font-normal">{total.toLocaleString()} records across</span>{' '}
                <span className="text-primary font-semibold">{buildings.length} buildings</span>
              </p>
              {/* Cards grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                {buildings.map((b) => (
                  <button
                    key={b.name}
                    onClick={() => setFilters((f) => ({ ...f, building: b.name }))}
                    className="text-left px-4 py-3 rounded-xl border border-border bg-white hover:border-primary hover:shadow-md transition-all group"
                  >
                    <p className="text-sm font-semibold text-primary-text truncate group-hover:text-primary">{b.name}</p>
                    <p className="text-xs text-primary mt-1 font-medium">{b.unitCount ?? 0} units</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Result count bar */}
          <div className="px-4 py-1.5 border-t border-border bg-[#f9fafb] flex items-center justify-between">
            <p className="text-xs text-secondary">
              {isFetching && !isLoading
                ? 'Updating…'
                : <><span className="font-semibold text-primary-text">{total.toLocaleString()}</span> results</>
              }
            </p>
          </div>
        </div>

        {activeView === 'list' && (
          <>
            {/* Table */}
            <div className="flex-1 overflow-auto relative z-0">
              <DataTable
                fixedLayout
                trailingActionsColumn={false}
                columns={ALL_COLUMNS}
                hiddenColumns={hiddenColumns}
                rows={items}
                rowKey={(r) => r.unitId}
                isLoading={isLoading}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                selectable
                allSelected={allSelected}
                onSelectAll={handleSelectAll}
                getRowClassName={(unit) =>
                  selectedRows.has(unit.unitId) ? 'bg-primary/5' : ''
                }
                getRowDataAttrs={(unit) => ({ 'data-unit-id': unit.unitId })}
                emptyState={
                  <div className="text-center py-4">
                    <Search className="w-8 h-8 text-secondary mx-auto mb-2" />
                    <p className="font-semibold text-primary-text">No units found</p>
                    <p className="text-xs text-secondary mt-1">Try adjusting search or filters</p>
                  </div>
                }
                renderRow={(unit, visibleColumns) => (
                  <UnitTableRow
                    key={unit.unitId}
                    unit={unit}
                    visibleColumns={visibleColumns}
                    selected={selectedRows.has(unit.unitId)}
                    onSelect={() => toggleRow(unit.unitId)}
                    onClick={() => setSelectedId(unit.landlordId)}
                    canEdit={hasRole('admin')}
                  />
                )}
              />
            </div>

            {/* Pagination footer */}
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        )}

        {activeView === 'stats' && (
          <div className="flex-1 overflow-auto relative z-0 p-6 bg-[#f9fafb] animate-fade-in">
            {statsLoading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-5 h-5 animate-spin text-secondary" />
              </div>
            ) : statsData ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Records by Area */}
                <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-border">
                    <h3 className="text-lg font-semibold text-primary">Records by Area</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-secondary uppercase tracking-wider">Area</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-secondary uppercase tracking-wider">Records</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-secondary uppercase tracking-wider">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsData.recordsByArea?.map((row: any) => (
                        <tr key={row.area} className="border-b border-border/50 hover:bg-hover-bg transition-colors">
                          <td className="py-3 px-4">
                            <button
                              onClick={() => {
                                const area = areas.find(a => a.name === row.area);
                                if (area) {
                                  setFilters(f => ({ ...f, area_id: String(area.id) }));
                                  setActiveView('list');
                                }
                              }}
                              className="text-sm text-primary hover:underline font-medium"
                            >
                              {row.area}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-sm font-bold text-primary-text">{Number(row.records).toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-secondary">{row.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Top Buildings */}
                <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-border">
                    <h3 className="text-lg font-semibold text-primary">Top Buildings</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-secondary uppercase tracking-wider">Building</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-secondary uppercase tracking-wider">Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsData.topBuildings?.map((row: any) => (
                        <tr key={row.building} className="border-b border-border/50 hover:bg-hover-bg transition-colors">
                          <td className="py-3 px-4">
                            <button
                              onClick={() => {
                                setFilters(f => ({ ...f, building: row.building }));
                                setActiveView('list');
                              }}
                              className="text-sm text-primary hover:underline font-medium"
                            >
                              {row.building}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-sm font-bold text-primary-text text-right">{Number(row.units).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {activeView === 'buildings' && (() => {
          // Sherzod 2026-05-03: Buildings view groups by area when no area
          // is filtered. Single-area selection keeps the flat grid.
          const isAllAreas = !filters.area_id;
          const totalBuildings = buildings.length;
          const totalRecords = buildings.reduce((s, b) => s + (b.unitCount ?? 0), 0);

          // Group + sort areas. Configurable sort: alpha or by record count.
          const grouped = isAllAreas ? (() => {
            const map = new Map<string, {
              areaId: number | null;
              areaName: string;
              buildings: typeof buildings;
              records: number;
            }>();
            for (const b of buildings) {
              const key = b.area_id != null ? String(b.area_id) : 'none';
              let entry = map.get(key);
              if (!entry) {
                entry = {
                  areaId: b.area_id,
                  areaName: b.areaName ?? 'Unknown area',
                  buildings: [],
                  records: 0,
                };
                map.set(key, entry);
              }
              entry.buildings.push(b);
              entry.records += b.unitCount ?? 0;
            }
            // Within each area, alphabetical
            map.forEach(e => e.buildings.sort((a, b) => a.name.localeCompare(b.name)));
            const list = Array.from(map.values()).filter(e => e.buildings.length > 0);
            // Area sort
            if (buildingsAreaSort === 'count') {
              list.sort((a, b) => b.records - a.records);
            } else {
              list.sort((a, b) => a.areaName.localeCompare(b.areaName));
            }
            return list;
          })() : null;

          const cardClass = "text-left px-4 py-3 rounded-xl border border-border bg-white hover:border-primary hover:shadow-md transition-all group";

          return (
            <div className="flex-1 overflow-auto relative z-0 p-6 animate-fade-in">
              {isAllAreas ? (
                <>
                  {/* Top summary bar */}
                  <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
                    <h3 className="text-lg font-semibold text-primary">
                      All Areas — <span className="text-primary-text">{totalRecords.toLocaleString()}</span> records across <span className="text-primary-text">{totalBuildings.toLocaleString()}</span> buildings
                    </h3>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-secondary">Sort areas:</span>
                      <button
                        onClick={() => setBuildingsAreaSort('alpha')}
                        className={cn(
                          'px-2.5 py-1 rounded-md font-medium transition-colors',
                          buildingsAreaSort === 'alpha'
                            ? 'bg-primary text-white'
                            : 'bg-white border border-border text-secondary hover:border-primary'
                        )}
                      >
                        A–Z
                      </button>
                      <button
                        onClick={() => setBuildingsAreaSort('count')}
                        className={cn(
                          'px-2.5 py-1 rounded-md font-medium transition-colors',
                          buildingsAreaSort === 'count'
                            ? 'bg-primary text-white'
                            : 'bg-white border border-border text-secondary hover:border-primary'
                        )}
                      >
                        By records
                      </button>
                    </div>
                  </div>

                  {grouped && grouped.length === 0 && (
                    <p className="text-sm text-secondary">No buildings in any area.</p>
                  )}

                  {grouped && grouped.map((g) => (
                    <section key={g.areaId ?? 'none'} className="mb-7">
                      <h4 className="text-sm font-semibold text-primary mb-3">
                        {g.areaName} — <span className="text-primary-text">{g.records.toLocaleString()}</span> records across{' '}
                        <button
                          onClick={() => {
                            // Filter the whole page down to this area.
                            if (g.areaId == null) return;
                            setFilters(f => ({ ...f, area_id: String(g.areaId) }));
                            // Stay on buildings view (single-area flat grid).
                          }}
                          className="text-primary hover:underline font-semibold disabled:no-underline disabled:cursor-default"
                          disabled={g.areaId == null}
                        >
                          {g.buildings.length} building{g.buildings.length === 1 ? '' : 's'}
                        </button>
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                        {g.buildings.map((b) => (
                          <button
                            key={`${g.areaId ?? 'none'}-${b.name}`}
                            onClick={() => {
                              setFilters(f => ({ ...f, building: b.name }));
                              setActiveView('list');
                            }}
                            className={cardClass}
                          >
                            <p className="text-sm font-semibold text-primary-text truncate group-hover:text-primary">{b.name}</p>
                            <p className="text-xs text-primary mt-1 font-medium">{(b.unitCount ?? 0).toLocaleString()} units</p>
                          </button>
                        ))}
                      </div>
                    </section>
                  ))}
                </>
              ) : (
                <>
                  {/* Single-area: flat grid (current behavior) */}
                  <h3 className="text-lg font-semibold text-primary mb-4">
                    {areas.find(a => String(a.id) === filters.area_id)?.name ?? 'Buildings'}
                    {' — '}
                    <span className="text-primary-text">{totalRecords.toLocaleString()}</span> records across{' '}
                    <span className="text-primary-text">{totalBuildings.toLocaleString()}</span> buildings
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                    {buildings.map((b) => (
                      <button
                        key={b.name}
                        onClick={() => {
                          setFilters(f => ({ ...f, building: b.name }));
                          setActiveView('list');
                        }}
                        className={cardClass}
                      >
                        <p className="text-sm font-semibold text-primary-text truncate group-hover:text-primary">{b.name}</p>
                        <p className="text-xs text-primary mt-1 font-medium">{(b.unitCount ?? 0).toLocaleString()} units</p>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Landlord detail drawer */}
      <LandlordDrawer
        landlordId={selectedId}
        onClose={() => setSelectedId(null)}
        onJumpToUnit={(unitId) => {
          // Sherzod 2026-05-06: close drawer + scroll-to-and-highlight
          // the matching row by data-unit-id. If the unit isn't on the
          // current page (filtered/paginated out) the row won't be in
          // the DOM — show a toast instead so the user knows why it
          // didn't move.
          setSelectedId(null);
          // wait for the drawer close animation + table re-render
          window.setTimeout(() => {
            const row = document.querySelector<HTMLElement>(`[data-unit-id="${unitId}"]`);
            if (!row) {
              toast(
                'Unit not on this page — adjust filters or pagination to find it.',
                'info'
              );
              return;
            }
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            row.classList.add('animate-row-flash');
            window.setTimeout(() => row.classList.remove('animate-row-flash'), 1500);
          }, 250);
        }}
      />
    </div>
  );
}

// Kebab menu for table-level actions
function KebabMenu({ onRefresh }: { onRefresh: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg border border-border text-secondary hover:border-primary hover:text-primary-text transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
        aria-label="More actions"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-border rounded-xl shadow-lg w-44 py-1 animate-popover-in">
          {/* Caret */}
          <div aria-hidden="true" className="absolute -top-[7px] right-3 w-3 h-3 bg-white border-l border-t border-border rotate-45" />
          <button
            onClick={() => { onRefresh(); setOpen(false); }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-primary-text hover:bg-hover-bg w-full text-left"
          >
            <RefreshCw className="w-3.5 h-3.5 text-secondary" />
            Refresh data
          </button>
        </div>
      )}
    </div>
  );
}

// Per-contact desktop table row (Phase 1 of Backlog 07, 2026-05-03).
// Returns Fragment of cells. Star is its own column, mobile is text-only,
// actions has the call+WA buttons, stage has the inline dropdown editor.
function LandlordTableRow({
  landlord,
  visibleColumns,
  selected,
  onSelect,
  onClick,
}: {
  landlord: LandlordListItem;
  visibleColumns: Array<{ key: string }>;
  selected: boolean;
  onSelect: () => void;
  onClick: () => void;
}) {
  const { logAndCall, logAndWhatsApp } = useActivityLog();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const starMutation = useMutation({
    mutationFn: () => landlordsApi.patch(landlord.id, { isStarred: !landlord.isStarred } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlords'] });
    },
  });

  // Stage dropdown (click the Status badge → 11 options popover)
  const [stageOpen, setStageOpen] = useState(false);
  const stageRef = useRef<HTMLTableCellElement>(null);
  useEffect(() => {
    if (!stageOpen) return;
    function handler(e: MouseEvent) {
      if (stageRef.current && !stageRef.current.contains(e.target as Node)) {
        setStageOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [stageOpen]);

  const stagePatch = useMutation({
    mutationFn: (newStage: string) =>
      landlordsApi.patch(landlord.id, { stage: newStage } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlords'] });
      setStageOpen(false);
      toast('Status updated', 'success');
    },
    onError: (err: Error) => {
      toast(err.message, 'error');
      setStageOpen(false);
    },
  });

  const primaryPhone = landlord.phone;
  const isMasked = primaryPhone ? primaryPhone.phone.includes('*') : true;
  const canCall = primaryPhone && primaryPhone.isMobile && !isMasked;
  const phoneDigits = primaryPhone ? primaryPhone.phone.replace(/^\+/, '').replace(/\s/g, '') : '';

  const colMap: Record<string, React.ReactNode> = {
    star: (
      <td className="px-2 py-3 text-center">
        <button
          onClick={(e) => { e.stopPropagation(); starMutation.mutate(); }}
          className={cn(
            'p-0.5 rounded transition-colors',
            landlord.isStarred ? 'text-yellow-400 hover:text-yellow-500' : 'text-gray-300 hover:text-yellow-400'
          )}
          aria-label={landlord.isStarred ? 'Unstar' : 'Star as distress deal'}
          title={landlord.needsReview ? 'Needs review' : undefined}
        >
          <Star className="w-4 h-4" fill={landlord.isStarred ? 'currentColor' : 'none'} />
        </button>
      </td>
    ),
    name: (
      <td className="px-4 py-3 cursor-pointer truncate" onClick={onClick} title={landlord.name}>
        <span className="font-medium text-primary-text text-sm truncate leading-tight block">
          {landlord.name}
        </span>
      </td>
    ),
    mobile: (
      <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-primary-text">
        {primaryPhone ? primaryPhone.phone : <span className="text-secondary">—</span>}
      </td>
    ),
    actions: (
      <td className="px-4 py-3 whitespace-nowrap">
        {primaryPhone && canCall ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={(e) => { e.stopPropagation(); logAndCall(landlord.id, primaryPhone.phone); }}
              className="p-1 rounded bg-success/10 text-success hover:bg-success/20 min-h-[28px] min-w-[28px] flex items-center justify-center"
              aria-label={`Call ${landlord.name}`}
            >
              <Phone className="w-3 h-3" />
            </button>
            <a
              href={`https://wa.me/${phoneDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); logAndWhatsApp(landlord.id, primaryPhone.phone); }}
              className="p-1 rounded bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 min-h-[28px] min-w-[28px] flex items-center justify-center"
              aria-label={`WhatsApp ${landlord.name}`}
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
          </div>
        ) : (
          <span className="text-secondary text-xs">—</span>
        )}
      </td>
    ),
    units: (
      <td className="px-4 py-3 cursor-pointer text-right tabular-nums" onClick={onClick}>
        <span className="text-sm font-semibold text-primary-text">{landlord.totalUnits}</span>
      </td>
    ),
    buildings: (
      <td className="px-4 py-3 cursor-pointer text-right tabular-nums" onClick={onClick}>
        <span className="text-sm text-secondary">{landlord.buildingCount}</span>
      </td>
    ),
    areas: (
      <td className="px-4 py-3 cursor-pointer text-right tabular-nums" onClick={onClick}>
        <span className="text-sm text-secondary">{landlord.areaCount}</span>
      </td>
    ),
    stage: (
      <td ref={stageRef} className="px-4 py-3 relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setStageOpen(o => !o); }}
          className="cursor-pointer hover:opacity-80 transition-opacity"
          aria-label="Change status"
          title="Click to change status"
        >
          <StageBadge stage={landlord.stage} />
        </button>
        {stageOpen && (
          <div
            className="absolute z-50 mt-1 bg-white border border-border rounded-xl shadow-lg py-1 min-w-[180px]"
            onClick={(e) => e.stopPropagation()}
          >
            {STAGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={stagePatch.isPending}
                onClick={() => stagePatch.mutate(opt.value)}
                className={cn(
                  'flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm transition-colors',
                  landlord.stage === opt.value
                    ? 'bg-primary/5 text-primary-text font-medium'
                    : 'text-primary-text hover:bg-hover-bg'
                )}
              >
                <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', STAGE_DOT_BG[opt.value])} />
                <span>{opt.label}</span>
                {landlord.stage === opt.value && (
                  <Check className="w-3.5 h-3.5 ml-auto text-primary" />
                )}
              </button>
            ))}
          </div>
        )}
      </td>
    ),
    last_activity: (
      <td className="px-4 py-3 cursor-pointer truncate text-xs text-secondary" onClick={onClick}>
        {formatDistanceToNow(new Date(landlord.updatedAt), { addSuffix: true })}
      </td>
    ),
    tags: (
      <td className="px-4 py-3 cursor-pointer truncate" onClick={onClick}>
        {landlord.tags.length > 0 ? (
          <div className="flex gap-1 flex-wrap">
            {landlord.tags.slice(0, 3).map((t) => (
              <span key={t.id} className="text-[10px] bg-primary/10 text-primary px-1.5 rounded-full">
                {t.name}
              </span>
            ))}
            {landlord.tags.length > 3 && (
              <span className="text-[10px] text-secondary">+{landlord.tags.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-secondary text-xs">—</span>
        )}
      </td>
    ),
  };

  return (
    <>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="w-4 h-4 rounded border-border accent-primary"
          aria-label={`Select ${landlord.name}`}
        />
      </td>
      {visibleColumns.map((col) => (
        <Fragment key={col.key}>{colMap[col.key] ?? <td />}</Fragment>
      ))}
    </>
  );
}

// =============================================================================
// UnitTableRow — desktop table row for /api/units (one row per unit).
// =============================================================================
function UnitTableRow({
  unit,
  visibleColumns,
  selected,
  onSelect,
  onClick,
  canEdit,
}: {
  unit: UnitListItem;
  visibleColumns: Array<{ key: string }>;
  selected: boolean;
  onSelect: () => void;
  onClick: () => void;
  canEdit: boolean;
}) {
  const { logAndCall, logAndWhatsApp } = useActivityLog();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const starMutation = useMutation({
    mutationFn: () =>
      landlordsApi.patch(unit.landlordId, { isStarred: !unit.isStarred } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });

  // Stage dropdown (click the Status badge → open popover with all 11 options)
  const [stageOpen, setStageOpen] = useState(false);
  const stageRef = useRef<HTMLTableCellElement>(null);
  useEffect(() => {
    if (!stageOpen) return;
    function handler(e: MouseEvent) {
      if (stageRef.current && !stageRef.current.contains(e.target as Node)) {
        setStageOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [stageOpen]);

  const stagePatch = useMutation({
    mutationFn: (newStage: string) =>
      unitsApi.patch(unit.unitId, { stage: newStage as import('@imza/shared').LandlordStage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
      setStageOpen(false);
      toast('Status updated', 'success');
    },
    onError: (err: Error) => {
      toast(err.message, 'error');
      setStageOpen(false);
    },
  });

  // Inline-edit state — admin double-clicks View or Beds cell to edit.
  const [editingField, setEditingField] = useState<'view' | 'beds' | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const editPatch = useMutation({
    mutationFn: (body: { view?: string | null; beds?: string | null }) =>
      landlordsApi.patchUnit(unit.unitId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
      setEditingField(null);
      toast('Unit updated', 'success');
    },
    onError: (err: Error) => {
      toast(err.message, 'error');
      setEditingField(null);
    },
  });

  function startEdit(field: 'view' | 'beds') {
    if (!canEdit) return;
    setEditingField(field);
    setEditingValue(field === 'view' ? unit.view ?? '' : unit.beds ?? '');
  }
  function commitEdit() {
    if (editingField === null) return;
    const trimmed = editingValue.trim();
    const next = trimmed === '' ? null : trimmed;
    const current = editingField === 'view' ? unit.view : unit.beds;
    if (next === current) {
      setEditingField(null);
      return;
    }
    editPatch.mutate({ [editingField]: next });
  }
  function cancelEdit() {
    setEditingField(null);
  }

  const primaryPhone = unit.primaryMobile;
  const isMasked = primaryPhone ? primaryPhone.phone.includes('*') : true;
  const canCall = primaryPhone && primaryPhone.isMobile && !isMasked;

  const colMap: Record<string, React.ReactNode> = {
    // ── Star: own column, single button only ──
    star: (
      <td className="px-2 py-3 text-center">
        <button
          onClick={(e) => { e.stopPropagation(); starMutation.mutate(); }}
          className={cn(
            'p-0.5 rounded transition-colors',
            unit.isStarred
              ? 'text-yellow-400 hover:text-yellow-500'
              : 'text-gray-300 hover:text-yellow-400'
          )}
          aria-label={unit.isStarred ? 'Unstar' : 'Star as distress deal'}
          title={unit.needsReview ? 'Needs review' : undefined}
        >
          <Star className="w-4 h-4" fill={unit.isStarred ? 'currentColor' : 'none'} />
        </button>
      </td>
    ),
    // ── Owner: just the owner name (star moved to its own column) ──
    name: (
      <td className="px-4 py-3 cursor-pointer truncate" onClick={onClick}>
        <span
          className="font-medium text-primary-text text-sm truncate leading-tight block"
          title={unit.ownerName}
        >
          {unit.ownerName}
        </span>
      </td>
    ),
    building: (
      <td
        className="px-4 py-3 text-sm cursor-pointer truncate"
        onClick={onClick}
        title={unit.building ? (unit.areaName ? `${unit.building} — ${unit.areaName}` : unit.building) : undefined}
      >
        {unit.building ? (
          <span className="text-primary-text">{unit.building}</span>
        ) : (
          <span className="text-secondary">—</span>
        )}
      </td>
    ),
    unit_no: (
      <td
        className="px-4 py-3 text-sm font-mono cursor-pointer truncate"
        onClick={onClick}
        title={unit.unitNo ?? undefined}
      >
        {unit.unitNo ?? <span className="text-secondary">—</span>}
      </td>
    ),
    view: (
      <td
        className={cn('px-4 py-3 text-xs', canEdit && 'cursor-pointer')}
        onDoubleClick={() => startEdit('view')}
        title={canEdit ? 'Double-click to edit' : undefined}
      >
        {editingField === 'view' ? (
          <input
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit();
              else if (e.key === 'Escape') cancelEdit();
            }}
            className="w-full px-1 py-0.5 border border-primary rounded text-xs"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          unit.view ?? <span className="text-secondary">—</span>
        )}
      </td>
    ),
    beds: (
      <td
        className={cn('px-4 py-3 text-xs truncate', canEdit && 'cursor-pointer')}
        onDoubleClick={() => startEdit('beds')}
        title={canEdit ? 'Double-click to edit' : (unit.beds ?? undefined)}
      >
        {editingField === 'beds' ? (
          <input
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit();
              else if (e.key === 'Escape') cancelEdit();
            }}
            className="w-full px-1 py-0.5 border border-primary rounded text-xs"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          unit.beds ?? <span className="text-secondary">—</span>
        )}
      </td>
    ),
    stage: (
      <td ref={stageRef} className="px-4 py-3 relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setStageOpen(o => !o); }}
          className="cursor-pointer hover:opacity-80 transition-opacity"
          aria-label="Change status"
          title="Click to change status"
        >
          <StageBadge stage={unit.stage} />
        </button>
        {stageOpen && (
          <div
            className="absolute z-50 mt-1 bg-white border border-border rounded-xl shadow-lg py-1 min-w-[180px]"
            onClick={(e) => e.stopPropagation()}
          >
            {STAGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={stagePatch.isPending}
                onClick={() => stagePatch.mutate(opt.value)}
                className={cn(
                  'flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm transition-colors',
                  unit.stage === opt.value
                    ? 'bg-primary/5 text-primary-text font-medium'
                    : 'text-primary-text hover:bg-hover-bg'
                )}
              >
                <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', STAGE_DOT_BG[opt.value])} />
                <span>{opt.label}</span>
                {unit.stage === opt.value && (
                  <Check className="w-3.5 h-3.5 ml-auto text-primary" />
                )}
              </button>
            ))}
          </div>
        )}
      </td>
    ),
    // 'phones' kept for back-compat with any saved sortBy='phones'; not in
    // ALL_COLUMNS anymore. The active phone column is 'phone' below.
    phones: (
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          {primaryPhone ? (
            <>
              <span className="text-xs font-mono text-primary-text whitespace-nowrap">
                {formatPhone(primaryPhone.phone)}
              </span>
              {canCall && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      logAndCall(unit.landlordId, primaryPhone.phone);
                    }}
                    className="p-1 rounded bg-success/10 text-success hover:bg-success/20 min-h-[28px] min-w-[28px] flex items-center justify-center"
                    aria-label={`Call ${unit.ownerName}`}
                  >
                    <Phone className="w-3 h-3" />
                  </button>
                  <a
                    href={`https://wa.me/${phoneToDigits(primaryPhone.phone).replace(/^\+/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                      logAndWhatsApp(unit.landlordId, primaryPhone.phone);
                    }}
                    className="p-1 rounded bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 min-h-[28px] min-w-[28px] flex items-center justify-center"
                    aria-label={`WhatsApp ${unit.ownerName}`}
                  >
                    <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </a>
                </>
              )}
            </>
          ) : (
            <span className="text-secondary text-xs">—</span>
          )}
        </div>
      </td>
    ),
    // ── Phone (text only) — separate from Actions for clean column alignment ──
    phone: (
      <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-primary-text">
        {primaryPhone ? formatPhone(primaryPhone.phone) : <span className="text-secondary">—</span>}
      </td>
    ),
    // ── Actions (Call + WhatsApp buttons only, no phone text) ──
    actions: (
      <td className="px-4 py-3 whitespace-nowrap">
        {primaryPhone && canCall ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                logAndCall(unit.landlordId, primaryPhone.phone);
              }}
              className="p-1 rounded bg-success/10 text-success hover:bg-success/20 min-h-[28px] min-w-[28px] flex items-center justify-center"
              aria-label={`Call ${unit.ownerName}`}
            >
              <Phone className="w-3 h-3" />
            </button>
            <a
              href={`https://wa.me/${phoneToDigits(primaryPhone.phone).replace(/^\+/, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                logAndWhatsApp(unit.landlordId, primaryPhone.phone);
              }}
              className="p-1 rounded bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 min-h-[28px] min-w-[28px] flex items-center justify-center"
              aria-label={`WhatsApp ${unit.ownerName}`}
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
          </div>
        ) : (
          <span className="text-secondary text-xs">—</span>
        )}
      </td>
    ),
    email: (
      <td className="px-4 py-3 text-xs whitespace-nowrap">
        {unit.primaryEmail ? (
          <a
            href={`mailto:${unit.primaryEmail}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary hover:underline truncate inline-block max-w-[180px]"
            title={unit.primaryEmail}
          >
            {unit.primaryEmail}
          </a>
        ) : (
          <span className="text-secondary">—</span>
        )}
      </td>
    ),
    imported: (
      <td className="px-4 py-3 text-xs text-secondary tabular-nums whitespace-nowrap">
        {format(new Date(unit.importedAt), 'yyyy-MM-dd')}
      </td>
    ),
    last_activity: (
      <td className="px-4 py-3 text-xs text-secondary whitespace-nowrap">
        {unit.lastActivityAt
          ? formatDistanceToNow(new Date(unit.lastActivityAt), { addSuffix: true })
          : '—'}
      </td>
    ),
    // Total units owned by the parent landlord (Sherzod 2026-05-04 — F&C
    // comparison). Helps spot multi-unit owners at a glance.
    total_units: (
      <td className="px-4 py-3 text-sm tabular-nums text-primary-text">
        {unit.landlordTotalUnits.toLocaleString()}
      </td>
    ),
    // Latest unit_comments body, truncated. Hover reveals full text.
    comment: (
      <td className="px-4 py-3 text-xs text-primary-text">
        {unit.latestComment ? (
          <span className="truncate inline-block max-w-[220px]" title={unit.latestComment}>
            {unit.latestComment}
          </span>
        ) : (
          <span className="text-secondary">—</span>
        )}
      </td>
    ),
  };

  // Returns a Fragment of <td> cells — DataTable wraps in the outer <tr>.
  // Selected-row highlight is applied via DataTable's `getRowClassName` prop
  // to AVOID nested <tr><tr>, which the browser silently ignores and which
  // breaks `table-layout: fixed` column widths (Sherzod 2026-05-03).
  return (
    <>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="w-4 h-4 rounded border-border accent-primary"
          aria-label={`Select unit ${unit.unitNo ?? unit.unitId}`}
        />
      </td>
      {visibleColumns.map((col) => (
        <Fragment key={col.key}>{colMap[col.key] ?? <td />}</Fragment>
      ))}
    </>
  );
}

// =============================================================================
// UnitMobileCard — mobile-layout card for one unit row.
// Compact view: owner + building/unit + stage chip + phone with call/WhatsApp.
// =============================================================================
function UnitMobileCard({
  unit,
  onClick,
}: {
  unit: UnitListItem;
  onClick: () => void;
}) {
  const { logAndCall, logAndWhatsApp } = useActivityLog();
  const primaryPhone = unit.primaryMobile;
  const isMasked = primaryPhone ? primaryPhone.phone.includes('*') : true;
  const canCall = primaryPhone && primaryPhone.isMobile && !isMasked;

  return (
    <button
      onClick={onClick}
      className="w-full bg-white border border-border rounded-xl p-3 text-left hover:border-primary/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-primary-text text-sm truncate flex items-center gap-1">
            {unit.isStarred && <Star className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" fill="currentColor" />}
            {unit.ownerName}
          </p>
          {unit.building && (
            <p className="text-xs text-secondary truncate mt-0.5">
              {unit.building}{unit.unitNo ? ` · ${unit.unitNo}` : ''}{unit.beds ? ` · ${unit.beds}` : ''}
            </p>
          )}
          {unit.areaName && (
            <p className="text-[10px] text-secondary mt-0.5 truncate">{unit.areaName}</p>
          )}
        </div>
        <StageBadge stage={unit.stage} />
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        {primaryPhone ? (
          <>
            <span className="text-xs font-mono text-primary-text flex-1 truncate">
              {formatPhone(primaryPhone.phone)}
            </span>
            {canCall && (
              <>
                <a
                  href={`tel:${phoneToDigits(primaryPhone.phone)}`}
                  onClick={(e) => { e.stopPropagation(); logAndCall(unit.landlordId, primaryPhone.phone); }}
                  className="p-1.5 rounded-lg bg-success/10 text-success min-h-[32px] min-w-[32px] flex items-center justify-center"
                  aria-label={`Call ${unit.ownerName}`}
                >
                  <Phone className="w-3.5 h-3.5" />
                </a>
                <a
                  href={`https://wa.me/${phoneToDigits(primaryPhone.phone).replace(/^\+/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.stopPropagation(); logAndWhatsApp(unit.landlordId, primaryPhone.phone); }}
                  className="p-1.5 rounded-lg bg-[#25D366]/10 text-[#25D366] min-h-[32px] min-w-[32px] flex items-center justify-center"
                  aria-label={`WhatsApp ${unit.ownerName}`}
                >
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>
              </>
            )}
          </>
        ) : (
          <span className="text-xs text-secondary">No phone</span>
        )}
      </div>
    </button>
  );
}
